import { useEffect, useState } from 'react';
import { Address } from 'react-daum-postcode';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';

import { z } from 'zod';

import { SignupDataType, usePostCheckJoinDuplicateApi, usePostSendEmailVerificationApi, usePostSignupApi, usePostVerifyCodeApi } from '@/api/auth';
import { PrimaryButton } from '@/components/common/Button/style';
import Field from '@/components/common/Field/Field';
import InputAddress from '@/components/common/Input/InputAddress/InputAddress';
import InputSelect from '@/components/common/Input/InputSelect/InputSelect';
import InputText from '@/components/common/Input/InputText/InputText';
import { emailOptions, monthOptions, yearOptions } from '@/constants/option';
import { useModalStore } from '@/store/modalStore';
import { formatCell } from '@/utils/formatting';

import * as S from './style';
import { useSignUpStore } from '../SignUpContext/SignUpContext';

interface RegisterField {
	name: string;
	enName: string;
	birth: { year: string; month: string; day: string };
	id: string;
	idCheck: boolean;
	password: string;
	passwordCheck: string;
	gender: string;
	address: { zipcode: string; address: string; detailAddress: string };
	cell: string;
	affiliation: string;
	researchField: string;
	studentNumber: string;
	email: { front: string; back: string; code: string; verificationCode: string };
}
const BasicInfoForm = () => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [isEmailVerified, setIsEmailVerified] = useState(false);
	const [isSendEmail, setIsSendEmail] = useState(false);
	const { updateBasicInfo, setStep, basicInfo, setCurrentStep } = useSignUpStore();
	const { showModal } = useModalStore();
	const { mutateAsync: Signup } = usePostSignupApi();
	const userRegisterSchema = z
		.object({
			name: z.string().min(1, { message: 'signup.basicInfo.validation.name' }),
			enName: z.string().min(1, { message: 'signup.basicInfo.validation.enName' }),
			birth: z.object({
				year: z.string().min(1, { message: 'signup.basicInfo.validation.birth' }),
				month: z.string().min(1, { message: 'signup.basicInfo.validation.birth' }),
				day: z.string().min(1, { message: 'signup.basicInfo.validation.birth' }),
			}),
			id: z
				.string()
				.min(1, { message: 'signup.basicInfo.validation.id' })
				.min(6, { message: 'signup.basicInfo.validation.id' })
				.max(20, { message: 'signup.basicInfo.validation.id' })
				.regex(/^[A-Za-z0-9]+$/, { message: 'signup.basicInfo.validation.id' }),
			idCheck: z.union([z.literal(true), z.literal(false)]).refine((val) => val === true, {
				message: 'signup.basicInfo.validation.idCheck',
			}),
			password: z
				.string()
				.min(1, { message: 'common.validation.passwordFormat' })
				.min(6, { message: 'common.validation.passwordFormat' })
				.max(20, { message: 'common.validation.passwordFormat' })
				.regex(/^[A-Za-z0-9!@#$%^&*()-_+=]+$/, { message: 'common.validation.passwordFormat' }),
			passwordCheck: z.string().min(1, { message: 'common.validation.passwordMismatch' }),
			email: z.object({
				front: z.string().min(1, { message: 'signup.basicInfo.validation.email' }),
				back: z.string().min(1, { message: 'signup.basicInfo.validation.email' }),
				code: z.string(),
				verificationCode: z.string(),
			}),
			gender: z.string().min(1, { message: 'signup.basicInfo.validation.gender' }),
			cell: z.string().min(1, { message: 'signup.basicInfo.validation.cell' }),
			affiliation: z.string().min(1, { message: 'signup.basicInfo.validation.affiliation' }),
			researchField: z.string().min(1, { message: 'education.validation.researchField' }),
			studentNumber: z.string(),
			address: z.object({
				zipcode: z.string().min(1, { message: 'signup.basicInfo.validation.zipcode' }),
				address: z.string().min(1, { message: 'signup.basicInfo.validation.address' }),
				detailAddress: z.string().min(1, { message: 'signup.basicInfo.validation.detailAddress' }),
			}),
		})
		.refine((data) => data.password === data.passwordCheck, {
			message: 'common.validation.passwordMismatch',
			path: ['passwordCheck'],
		});

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		setError,
		formState: { errors },
	} = useForm<RegisterField>({
		resolver: zodResolver(userRegisterSchema),
		mode: 'onChange',
		defaultValues: {
			name: '',
			enName: '',
			idCheck: false,
			birth: { year: '', month: '', day: '' },
			id: '',
			cell: '',
			password: '',
			passwordCheck: '',
			email: { front: '', back: '', code: '직접입력', verificationCode: '' },
			gender: '남',
			address: { zipcode: '', address: '', detailAddress: '' },
			affiliation: '',
			researchField: '',
			studentNumber: '',
		},
	});

	const { onChange, ...rest } = register('email.verificationCode');

	const { mutateAsync: VerifyCode } = usePostVerifyCodeApi();
	const { mutateAsync: SendEmailVerification, isPending } = usePostSendEmailVerificationApi();
	const { mutateAsync: CheckJoinDuplicate } = usePostCheckJoinDuplicateApi();
	const getLastDayOfMonth = (year: string, month: string) => {
		const date = new Date(Number(year), Number(month), 0);
		return date.getDate();
	};
	const days = Array.from({ length: getLastDayOfMonth(watch('birth.year'), watch('birth.month')) }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));
	const handleBirthYear = (label: string) => {
		setValue('birth.year', label, { shouldValidate: true });
	};
	const handleBirthMonth = (label: string) => {
		setValue('birth.month', label, { shouldValidate: true });
	};
	const handleBirthDay = (label: string) => {
		setValue('birth.day', label, { shouldValidate: true });
	};
	const handleAddress = (val: Address) => {
		setValue(
			'address',
			{
				zipcode: val.zonecode,
				address: val.address,
				detailAddress: '',
			},
			{ shouldValidate: true }
		);
	};

	const onSubmit = async (data: RegisterField) => {
		updateBasicInfo(data);

		const params: SignupDataType = {
			member_name: data.name,
			member_en_name: data.enName,
			member_birth_year: data.birth.year,
			member_birth_month: data.birth.month,
			member_birth_date: data.birth.day,
			member_gender: data.gender,
			member_cell: data.cell.replace(/-/g, ''),
			member_id: data.id,
			member_password: data.password,
			member_re_password: data.passwordCheck,
			member_zipcode: data.address.zipcode,
			member_address: data.address.address,
			member_address_detail: data.address.detailAddress,
			member_email: `${data.email.front}@${data.email.back}`,
			member_belong: data.affiliation,
			member_research_field: data.researchField,
			member_student_number: data.studentNumber,
		};

		try {
			const res = await Signup(params);
			if (res.data.success) {
				setStep('회원가입 완료');
				setCurrentStep('회원가입 완료');
				return;
			}
			showModal(t('common.alerts.registerFailed'));
		} catch (error) {
			console.error('Signup API request failed:', error);
			showModal(t('common.alerts.registerFailed'));
		}
	};

	const resetEmailVerification = () => {
		setIsSendEmail(false);
		setIsEmailVerified(false);
		setValue('email.verificationCode', '');
	};

	const handleSendEmailVerification = async () => {
		const res = await SendEmailVerification({ email: `${watch('email.front')}@${watch('email.back')}` });
		if (res.data.success) {
			showModal(t('common.alerts.emailVerificationSent'));
			setIsSendEmail(true);
			setIsEmailVerified(false);
			setValue('email.verificationCode', '');
		} else {
			showModal(t('common.alerts.emailVerificationFailed'));
			resetEmailVerification();
		}
	};
	const handleVerifyCode = async () => {
		const email = `${watch('email.front')}@${watch('email.back')}`;
		const code = watch('email.verificationCode');
		const res = await VerifyCode({ email, code });
		if (res.data.success) {
			showModal('인증이 완료되었습니다.');
			setIsEmailVerified(true);
		} else {
			showModal('인증번호가 일치하지 않습니다.');
			setIsEmailVerified(false);
		}
	};
	const handleCheckId = async () => {
		const res = await CheckJoinDuplicate({
			param: watch('id'),
			param_type: 'id',
		});
		if (res.data.success) {
			showModal('사용할 수 있는 아이디입니다.');
			setValue('idCheck', true, { shouldValidate: true }); // 확인 완료로 세팅
		} else {
			showModal('사용할 수 없는 아이디입니다.');
			setValue('idCheck', false, { shouldValidate: true });
			setError('id', {
				type: 'manual',
				message: '사용할 수 없는 아이디입니다.',
			});
		}
	};
	useEffect(() => {
		const hasInfo = basicInfo.name !== '' || basicInfo.enName !== '' || basicInfo.id !== '';

		if (hasInfo) {
			setValue('name', basicInfo.name);
			setValue('enName', basicInfo.enName);
			setValue('birth.year', basicInfo.birth.year);
			setValue('birth.month', basicInfo.birth.month);
			setValue('birth.day', basicInfo.birth.day);
			setValue('id', basicInfo.id);
			setValue('idCheck', !!basicInfo.id);
			setValue('cell', basicInfo.cell);
			setValue('password', basicInfo.password);
			setValue('passwordCheck', basicInfo.passwordCheck);
			setValue('address.address', basicInfo.address.address);
			setValue('address.zipcode', basicInfo.address.zipcode);
			setValue('address.detailAddress', basicInfo.address.detailAddress);
			setValue('gender', basicInfo.gender);
			setValue('email.front', basicInfo.email.front);
			setValue('email.back', basicInfo.email.back);
			setValue('email.code', basicInfo.email.code);
			setValue('affiliation', basicInfo.affiliation);
			setValue('researchField', basicInfo.researchField);
			setValue('studentNumber', basicInfo.studentNumber);
		}
	}, [basicInfo, setValue]);
	return (
		<S.FormContainer onSubmit={handleSubmit(onSubmit)}>
			<S.FieldBox>
				<S.Danger>{t('common.requiredNotice')}</S.Danger>
				<Field title="signup.basicInfo.fields.name" error={errors.name?.message ?? ''}>
					<InputText placeholder="signup.basicInfo.placeholders.name" {...register('name')} isError={!!errors.name?.message} />
				</Field>
				<Field title="signup.basicInfo.fields.enName" error={errors.enName?.message ?? ''}>
					<InputText placeholder="signup.basicInfo.placeholders.enName" {...register('enName')} isError={!!errors.enName?.message} />
				</Field>
				<Field title="signup.basicInfo.fields.birth" error={errors.birth?.year?.message ?? errors.birth?.day?.message ?? errors.birth?.month?.message ?? ''}>
					<S.BirthRow>
						<InputSelect options={yearOptions} selectedOption={watch('birth.year')} setSelectedOption={handleBirthYear} defaultValue="common.date.year" />
						<InputSelect options={monthOptions} selectedOption={watch('birth.month')} setSelectedOption={handleBirthMonth} defaultValue="common.date.month" />
						<InputSelect options={days} selectedOption={watch('birth.day')} setSelectedOption={handleBirthDay} defaultValue="common.date.day" />
					</S.BirthRow>
				</Field>
				<Field title="signup.basicInfo.fields.gender" error={errors.gender?.message ?? ''} gap="0.8rem">
					<S.GenderBox>
						<S.GenderRadio $active={watch('gender') === '남'}>
							<input type="radio" value="남" {...register('gender')} />
							{t('common.gender.male')}
						</S.GenderRadio>
						<S.GenderRadio $active={watch('gender') === '여'}>
							<input type="radio" value="여" {...register('gender')} />
							{t('common.gender.female')}
						</S.GenderRadio>
					</S.GenderBox>
				</Field>
				<Field title="signup.basicInfo.fields.id" error={errors.id?.message ?? errors.idCheck?.message ?? ''}>
					<S.AddressBox>
						<InputText
							{...register('id')}
							input={(e) => {
								register('id').onChange(e);
								setValue('idCheck', false, { shouldValidate: true });
							}}
							placeholder="signup.basicInfo.placeholders.id"
							isError={!!errors.id?.message}
						/>
						<S.FindButton type="button" onClick={handleCheckId} disabled={!!errors.id?.message || watch('id') === ''}>
							{t('common.actions.confirm')}
						</S.FindButton>
					</S.AddressBox>
				</Field>
				<Field title="signup.basicInfo.fields.password" error={errors.password?.message ?? ''}>
					<InputText type="password" {...register('password')} placeholder="common.placeholders.password" isError={!!errors.password?.message} />
				</Field>
				<Field title="signup.basicInfo.fields.passwordConfirm" error={errors.passwordCheck?.message ?? ''}>
					<InputText
						type="password"
						{...register('passwordCheck')}
						placeholder="common.placeholders.passwordConfirm"
						isError={!!errors.passwordCheck?.message}
					/>
				</Field>
				<Field title="signup.basicInfo.fields.address" error={errors.address?.address?.message ?? errors.address?.detailAddress?.message ?? ''}>
					<S.AddressContentBox>
						<S.AddressBox>
							<InputText readonly {...register('address.zipcode')} />
							<S.FindButton type="button" onClick={() => setIsOpen(true)}>
								{t('common.actions.findZipcode')}
							</S.FindButton>
						</S.AddressBox>
						<S.AddressBox>
							<InputText readonly {...register('address.address')} />
						</S.AddressBox>
						<S.AddressBox>
							<InputText
								{...register('address.detailAddress')}
								placeholder="common.placeholders.detailAddress"
								isError={!!errors.address?.address?.message || !!errors.address?.detailAddress?.message}
							/>
						</S.AddressBox>
					</S.AddressContentBox>
				</Field>
				<Field title="signup.basicInfo.fields.cell" error={errors.cell?.message ?? ''}>
					<InputText
						{...Object.assign({}, register('cell'), {
							onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
								const value = formatCell(e);
								setValue('cell', value, { shouldValidate: true });
							},
						})}
						placeholder="common.placeholders.phone"
						isError={!!errors.cell?.message}
					/>
				</Field>
				<Field title="signup.basicInfo.fields.affiliation" error={errors.affiliation?.message ?? ''}>
					<InputText {...register('affiliation')} placeholder="signup.basicInfo.placeholders.affiliation" isError={!!errors.affiliation?.message} />
				</Field>
				<Field title="signup.educationInfo.fields.researchField" error={errors.researchField?.message ?? ''}>
					<InputText {...register('researchField')} placeholder="education.placeholders.researchField" isError={!!errors.researchField?.message} />
				</Field>
				<Field title="education.fields.studentNumber" error={errors.studentNumber?.message ?? ''}>
					<InputText {...register('studentNumber')} placeholder="education.placeholders.studentNumber" isError={!!errors.studentNumber?.message} />
				</Field>
				<Field title="signup.basicInfo.fields.emailVerification" gap="0.8rem">
					<Field error={errors.email?.back?.message ?? errors.email?.front?.message ?? errors.email?.code?.message ?? ''}>
						<S.EmailRow>
							<InputText
								{...Object.assign({}, register('email.front'), {
									onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
										register('email.front').onChange(e);
										resetEmailVerification();
									},
								})}
								isError={!!errors.email?.front?.message}
							/>
							<span>@</span>
							<InputText
								input={(e: React.ChangeEvent<HTMLInputElement>) => {
									setValue('email.back', e.target.value, { shouldValidate: true });
									resetEmailVerification();
								}}
								value={watch('email.code') !== '' && watch('email.code') !== '직접입력' ? watch('email.code') : watch('email.back')}
								readonly={watch('email.code') !== '' && watch('email.code') !== '직접입력'}
							/>
							<InputSelect
								options={emailOptions}
								selectedOption={watch('email.code')}
								setSelectedOption={(val) => {
									setValue('email.code', val, { shouldValidate: true });
									resetEmailVerification();
									if (val !== '직접입력') setValue('email.back', val, { shouldValidate: true });
								}}
								defaultValue="common.directInput"
							/>
							<S.FindButton
								type="button"
								onClick={handleSendEmailVerification}
								disabled={watch('email.front') === '' || watch('email.back') === '' || watch('email.code') === '' || isPending}
							>
								{!isSendEmail ? t('common.actions.send') : t('common.actions.resend')}
							</S.FindButton>
						</S.EmailRow>
					</Field>
					{isSendEmail && (
						<>
							<S.FlexRow>
								<InputText
									{...rest}
									input={(e) => {
										onChange(e);
										setIsEmailVerified(false);
									}}
									placeholder="common.placeholders.verificationCode"
								/>
								<S.FindButton type="button" onClick={handleVerifyCode} disabled={watch('email.verificationCode') === '' || isEmailVerified}>
									{t('common.actions.verify')}
								</S.FindButton>
							</S.FlexRow>
							<S.Danger>{t('common.emailVerificationGuide')}</S.Danger>
						</>
					)}
				</Field>
			</S.FieldBox>
			<S.Actions>
				<PrimaryButton
				type="submit"
				$fullWidth
				disabled={
					!isEmailVerified ||
					watch('name') === '' ||
					watch('enName') === '' ||
					watch('birth.year') === '' ||
					watch('birth.month') === '' ||
					watch('birth.day') === '' ||
					watch('id') === '' ||
					watch('cell') === '' ||
					watch('password') === '' ||
					watch('passwordCheck') === '' ||
					watch('affiliation') === '' ||
					watch('researchField') === '' ||
					watch('email.front') === '' ||
					watch('email.back') === '' ||
					watch('email.code') === '' ||
					(isSendEmail && watch('email.verificationCode') === '') ||
					watch('address.zipcode') === '' ||
					watch('address.address') === '' ||
					watch('address.detailAddress') === ''
				}
			>
				{t('회원가입')}
			</PrimaryButton>
			</S.Actions>
			{isOpen && <InputAddress setIsOpen={setIsOpen} setAddress={handleAddress} />}
		</S.FormContainer>
	);
};

export default BasicInfoForm;
